import { useEffect, useRef, useState } from "react";

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';

    const cleaned = String(value)
        .replace(/\$/g, '')
        .replace(/,/g, '')
        .trim();

    if (cleaned === '') return '';

    const num = Number(cleaned);

    if (isNaN(num)) return value;

    return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
    });
};

const isPercentField = (field) => {
    if (!field) return false;

    return (
        String(field.id) === "389" ||
        String(field.name || "").includes("%") ||
        String(field.shortLabel || "").includes("%")
    );
};

const shouldFormatAsCurrency = (field) => {
    if (!field) return false;

    if (isPercentField(field)) return false;

    return (
        field.datatype === "decimal" ||
        field.datatype === "money" ||
        field.datatype === "currency"
    );
};

const getDisplayValue = (value, field) => {
    if (value === null || value === undefined) return "";

    if (shouldFormatAsCurrency(field)) {
        return formatCurrency(value);
    }

    return value;
};

const unformatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';

    return String(value)
        .replace(/\$/g, '')
        .replace(/,/g, '')
        .trim();
};

const FEE_DESCRIPTION_OPTIONS = [
    "2nd Appraisal Fee",
    "Administration Fee",
    "Application Fee",
    "Appraisal Desk Review Fee",
    "Appraisal Fee",
    "Appraisal Field Review Fee",
    "Appraisal Review Fee",
    "Broker Fee",
    "Commitment Fee",
    "Credit Report Fee",
    "Doc Prep Fee",
    "Document Redraw Fee",
    "Entity Review Fee",
    "Flood Certification Fee",
    "Flood Determination Fee",
    "HOA Certification Handling Fee",
    "HOA Questionnaire Fee",
    "Homebuyer Course",
    "Lender Compensation Credit",
    "Lender Credit",
    "Loan Discount Points",
    "Loan Origination Fee",
    "Origination Credit",
    "Origination Points",
    "Our Origination Charge",
    "Processing Fee",
    "Reinspection Fee",
    "Rush Fee",
    "Subordination Fee",
    "Tax Related Service Fee",
    "Third Party Processing Fee",
    "Translator Services",
    "Underwriting Fee",
    "Verification Fee",
    "VOE Fee",
    "Yield Spread Premium/Rebate",
    "Your Credit for the Interest Rate Chosen"
];

const SETTLEMENT_DESCRIPTION_OPTIONS = [
    "Attorney's Fees-Borower Chosen",
    "Attorney's Fees-Borower's Attorney",
    "Bond Review Fee",
    "Broker Cur",
    "Disbursement Fee",
    "FL Assessment Recoupment Surcharger",
    "HOA Certification Handling Fee",
    "HOA Tasfer/Deposit/Keys",
    "Home Warranty Premium",
    "Homeowner Dues",
    "Inspection Fe",
    "Pest Inspection Fee",
    "RE Transaction Coordinator Fee",
    "Real Estate Commission",
    "Realtor Admin Fee",
    "Realtor Processing Fee",
    "Survey Fee",
    "TN Service Charge",
    "Translator Services",
    "Utilities",
    "Water Allotment Payment"
];

const FEE_DESCRIPTION_COMBO_FIELD_IDS = [
    "1627",
    "1841",
    "NEWHUD.X130",
    "369",
    "371",
    "348",
    "931",
    "1390"
];

const SETTLEMENT_DESCRIPTION_COMBO_FIELD_IDS = [
    "NEWHUD.X251",
    "650",
    "651",
    "40",
    "43"
];

const BUSINESS_RULE_COMBO_OPTIONS = {
    "369": [
        "Subordination Fee"
    ],
    "1841": [
        "Loan Origination Fee"
    ],
    "1838": [
        "Entity Review Fee"
    ],
    "NEWHUD.X129": [
        "Third Party Processing Fee"
    ],
    "NEWHUD.X130": [
        "HOA Questionnaire Fee"
    ],
    "154": [
        "Doc Prep Fee"
    ],
    "1627": [
        "Commitment Fee"
    ],
    "NEWHUD.X127": [
        "2nd Appraisal Fee"
    ],
    "NEWHUD.X128": [
        "Homebuyer Course"
    ],
    "371": [
        "VOE Fee"
    ],
    "NEWHUD.X126": [
        "Appraisal Review Fee"
    ]
};

const getComboBoxOptions = (fieldId) => {
    const id = String(fieldId);

    // Priority 1: Encompass business rule option
    if (BUSINESS_RULE_COMBO_OPTIONS[id]) {
        return BUSINESS_RULE_COMBO_OPTIONS[id];
    }

    // Priority 2: Existing default fee description dropdown
    if (FEE_DESCRIPTION_COMBO_FIELD_IDS.includes(id)) {
        return FEE_DESCRIPTION_OPTIONS;
    }

    // Priority 3: Existing default settlement description dropdown
    if (SETTLEMENT_DESCRIPTION_COMBO_FIELD_IDS.includes(id)) {
        return SETTLEMENT_DESCRIPTION_OPTIONS;
    }

    return null;
};

const EditableComboBox = ({ id, value, field, ariaLabel, readOnly }) => {
    const [inputValue, setInputValue] = useState(value || "");
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const options = getComboBoxOptions(id) || [];

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSelect = (selectedValue) => {
        setInputValue(selectedValue);
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} className={`relative overflow-visible ${open ? "z-[9999]" : "z-0"}`}>
            <div className="flex">
                <input
                    id={`field-${id}`}
                    type="text"
                    readOnly={readOnly}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className={`form-input w-full rounded-r-none ${readOnly ? "cursor-not-allowed bg-gray-100" : ""}`}
                    aria-label={ariaLabel || field?.name || id}
                    title={field?.description || ""}
                    autoComplete="off"
                />

                <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => setOpen(prev => !prev)}
                    className={`w-8 min-w-[36px] border border-l-0 rounded-r bg-white text-gray-600 text-[10px] leading-none flex items-center justify-center ${readOnly ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-50"}`}
                    style={{
                        borderColor: "rgb(140, 147, 166)"
                    }}
                    tabIndex={-1}
                >
                    ▼
                </button>
            </div>

            {open && !readOnly && (
                <div className="absolute left-0 top-full z-[9999] mt-1 w-full max-h-[260px] overflow-y-auto rounded border border-gray-300 bg-white shadow-lg">
                    {options.map((option) => (
                        <div
                            key={option}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(option);
                            }}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const FieldInput = ({ id, value, field, ariaLabel, fieldValues = {} }) => {
    const isPaidToLender =
        field?.paidToField &&
        String(fieldValues[field.paidToField] || "").trim().toLowerCase() === "lender";

    const readOnly = !!field?.readonly || !!isPaidToLender;
    const isCurrencyField = shouldFormatAsCurrency(field);
    const defaultVal = getDisplayValue(value, field);
    const comboOptions = getComboBoxOptions(id);

    if (comboOptions) {
        return (
            <EditableComboBox
                id={id}
                value={value ?? ""}
                field={field}
                ariaLabel={ariaLabel}
                readOnly={readOnly}
            />
        );
    }

    const handleFocus = (e) => {
        if (readOnly || !isCurrencyField) return;

        e.target.value = unformatCurrency(e.target.value);
    };

    const handleBlur = (e) => {
        if (readOnly || !isCurrencyField) return;

        e.target.value = formatCurrency(e.target.value);
    };

    return (
        <input
            id={`field-${id}`}
            type="text"
            readOnly={readOnly}
            data-paid-to-field={field?.paidToField || ""}
            data-disabled-by-paid-to-lender={isPaidToLender ? "true" : "false"}
            className={`form-input w-full ${isCurrencyField ? "text-right" : ""} ${readOnly ? "cursor-not-allowed bg-gray-100" : ""}`}
            defaultValue={defaultVal}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-label={ariaLabel || field?.name || id}
            title={isPaidToLender ? "Disabled because Paid To value is Lender" : field?.description || ""}
        />
    );
};

const SECTION_DEFS = [
    {
        title: "A. Origination Charges",
        rows: [
            {
                type: "single",
                label: "Application Fees",
                fields: [{ id: "L228" }]
            },
            {
                type: "single",
                label: "Processing Fees",
                fields: [{ id: "1621" }]
            },
            {
                type: "single",
                label: "Underwriting Fees",
                fields: [{ id: "367" }]
            },
            {
                type: "pair",
                label: "Broker Fees",
                fields: [
                    { id: "389", label: "Broker Fees %" },
                    { id: "439", label: "$" }
                ]
            },
            {
                type: "pair",
                label: "Misc. Origination Charge 1",
                fields: [
                    { id: "NEWHUD.X1045", label: "Paid To Name" },
                    { id: "155", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Misc. Origination Charge 2",
                fields: [
                    { id: "NEWHUD.X1047", label: "Paid To Name" },
                    { id: "1839", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Misc. Origination Charge 3",
                fields: [
                    { id: "1627", label: "Description" },
                    { id: "1625", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Misc. Origination Charge 4",
                fields: [
                    { id: "1841", label: "Description" },
                    { id: "1842", label: "Amount" }
                ]
            }
        ]
    },
    {
        title: "B. Services You Cannot Shop For",
        rows: [
            {
                type: "single",
                label: "Appraisal Fee",
                fields: [{ id: "641" }]
            },
            {
                type: "single",
                label: "Credit Report Fee",
                fields: [{ id: "640" }]
            },
            {
                type: "single",
                label: "Appraisal Review Fee",
                fields: [{ id: "NEWHUD.X136" }]
            },
            {
                type: "single",
                label: "2nd Appraisal Fee",
                fields: [{ id: "NEWHUD.X137" }]
            },
            {
                type: "single",
                label: "Third Party Processing Fee",
                fields: [{ id: "NEWHUD.X139" }]
            },
            {
                type: "pair",
                label: "HOA Questionnaire",
                fields: [
                    { id: "NEWHUD.X130", label: "Description" },
                    { id: "NEWHUD.X140", label: "Fee" }
                ]
            },
            {
                type: "pair",
                label: "Service Charge 1",
                fields: [
                    { id: "369", label: "Description" },
                    { id: "370", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Service Charge 2",
                fields: [
                    { id: "371", label: "Description" },
                    { id: "372", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Service Charge 3",
                fields: [
                    { id: "348", label: "Description" },
                    { id: "349", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Service Charge 4",
                fields: [
                    { id: "931", label: "Description" },
                    { id: "932", label: "Amount" }
                ]
            },
            {
                type: "pair",
                label: "Service Charge 5",
                fields: [
                    { id: "1390", label: "Description" },
                    { id: "1009", label: "Amount" }
                ]
            }
        ]
    },
    {
        title: "F. Prepaids",
        rows: [
            {
                type: "single",
                label: "Interest Total Amount",
                fields: [{ id: "334" }]
            }
        ]
    },
    {
        title: "G. Initial Escrow Payment at Closing",
        rows: [
            {
                type: "subheader",
                label: "Homeowner's Ins."
            },
            {
                type: "triple",
                label: "Homeowner's Ins.",
                fields: [
                    { id: "1387", label: "No of Months" },
                    { id: "230", label: "Monthly Amount" },
                    { id: "656", label: "Total Amount" }
                ]
            },
            {
                type: "subheader",
                label: "Property Taxes"
            },
            {
                type: "triple",
                label: "Property Taxes",
                fields: [
                    { id: "1386", label: "No of Months" },
                    { id: "231", label: "Monthly Amount" },
                    { id: "655", label: "Total Amount" }
                ]
            },
            {
                type: "subheader",
                label: "Flood Insurance Reserve"
            },
            {
                type: "triple",
                label: "Flood Insurance Reserve",
                fields: [
                    { id: "1388", label: "No of Months" },
                    { id: "235", label: "Monthly Amount" },
                    { id: "657", label: "Total Amount" }
                ]
            }
        ]
    },
    {
        title: "Government Recording and Transfer Charges",
        rows: [
            {
                type: "single",
                label: "Recording Fees and Other Taxes",
                fields: [{ id: "NEWHUD.X607" }]
            },
            {
                type: "single",
                label: "Recording Fees",
                fields: [{ id: "390" }]
            },
            {
                type: "single",
                label: "Transfer Taxes",
                fields: [{ id: "NEWHUD.X731" }]
            },
            {
                type: "single",
                label: "Transfer Tax 1",
                fields: [{ id: "647" }]
            },
            {
                type: "single",
                label: "Transfer Tax 2",
                fields: [{ id: "648" }]
            },
            {
                type: "single",
                label: "Additional Recording Amount 1",
                fields: [{ id: "374" }]
            },
            {
                type: "single",
                label: "Additional Recording Amount 2",
                fields: [{ id: "1641" }]
            },
            {
                type: "single",
                label: "Additional Recording Amount 3",
                fields: [{ id: "1644" }]
            }
        ]
    },
    {
        title: "Additional Settlement Charges",
        rows: [
            {
                type: "triple",
                label: "Settlement Charge 1",
                fields: [
                    { id: "NEWHUD.X251", label: "Description" },
                    { id: "NEWHUD.X1085", label: "Paid To Name" },
                    { id: "NEWHUD.X254", label: "Amount" }
                ]
            },
            {
                type: "triple",
                label: "Settlement Charge 2",
                fields: [
                    { id: "650", label: "Description" },
                    { id: "NEWHUD.X1086", label: "Paid To Name" },
                    { id: "644", label: "Amount" }
                ]
            },
            {
                type: "triple",
                label: "Settlement Charge 3",
                fields: [
                    { id: "651", label: "Description" },
                    { id: "NEWHUD.X1087", label: "Paid To Name" },
                    { id: "645", label: "Amount" }
                ]
            },
            {
                type: "triple",
                label: "Settlement Charge 4",
                fields: [
                    { id: "40", label: "Description" },
                    { id: "NEWHUD.X1088", label: "Paid To Name" },
                    { id: "41", label: "Amount" }
                ]
            },
            {
                type: "triple",
                label: "Settlement Charge 5",
                fields: [
                    { id: "43", label: "Description" },
                    { id: "NEWHUD.X1089", label: "Paid To Name" },
                    { id: "44", label: "Amount" }
                ]
            }
        ]
    }
];

const FeeBreakdownV2Controls = ({ Fields = [], fieldValues = {} }) => {
    const findField = (id) => Fields.find((f) => String(f.id) === String(id));

    const renderSection = (section) => {
        return (
            <div
                key={section.title}
                className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] overflow-visible"
            >
                <div className="section-header px-4 py-2">
                    {section.title}
                </div>

                <div className="px-4 py-4">
                    {section.rows.map((row, index) => renderRow(row, `${section.title}-${index}`))}
                </div>
            </div>
        );
    };

    const renderRow = (row, key) => {
        if (row.type === "subheader") {
            return (
                <div key={key} className="pt-3 pb-1">
                    <div className="text-sm font-semibold text-gray-700 border-b border-gray-300 pb-1">
                        {row.label}
                    </div>
                </div>
            );
        }

        const hasFieldLabel = row.fields?.some(x => x.label?.trim());
        const isSingleRowWithoutFieldLabel = row.type === "single" && !hasFieldLabel;

        return (
            <div
                key={key}
                className={`fee-breakdown-row grid gap-3 items-start pt-1 ${isSingleRowWithoutFieldLabel ? "pb-3" : "pb-1"}`}
                style={{
                    gridTemplateColumns: "260px minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)"
                }}
            >
                <div className={`form-label-13 text-sm pr-2 ${hasFieldLabel ? "pt-7" : ""}`}>
                    {row.label}
                </div>

                {renderFieldSlots(row)}
            </div>
        );
    };

    const renderFieldSlots = (row) => {
        const fields = row.fields || [];
        const slots = [];

        for (let i = 0; i < 3; i++) {
            const fieldDef = fields[i];

            if (!fieldDef) {
                slots.push(<div key={`empty-${i}`} />);
                continue;
            }

            const field = findField(fieldDef.id) || {
                id: fieldDef.id,
                name: fieldDef.label || fieldDef.id,
                readonly: false
            };

            slots.push(
                <div key={fieldDef.id}>
                    {fieldDef.label?.trim() && (
                        <label className="form-label-13 block text-sm mb-1">
                            {fieldDef.label}
                        </label>
                    )}

                    <FieldInput
                        id={fieldDef.id}
                        value={fieldValues[fieldDef.id] ?? ""}
                        field={field}
                        fieldValues={fieldValues}
                        ariaLabel={fieldDef.label || field.name || fieldDef.id}
                    />

                    {/* <div className="text-[11px] text-gray-500 mt-1">
                        {fieldDef.id}
                    </div> */}
                </div>
            );
        }

        return slots;
    };

    return (
        <>
            <style>
                {`
                    input[type="number"]::-webkit-inner-spin-button,
                    input[type="number"]::-webkit-outer-spin-button {
                        -webkit-appearance: none;
                    }

                    input[type="number"] {
                        -moz-appearance: textfield;
                    }

                    @media (max-width: 1100px) {
                        .fee-breakdown-row {
                            grid-template-columns: 1fr !important;
                        }

                        .fee-breakdown-row .form-label-13 {
                            padding-top: 0 !important;
                        }
                    }
                `}
            </style>

            <div className="space-y-6">
                {SECTION_DEFS.map(renderSection)}
            </div>
        </>
    );
};

export default FeeBreakdownV2Controls;